/**
 * FilterPopover — anchored popup for adding a column filter.
 *
 * Props:
 *   column       string         — column being filtered
 *   anchorRect   DOMRect        — bounding rect of the header cell to anchor to
 *   initialOp    string         — optional starting operator (default 'like')
 *   initialValue string         — optional starting value (e.g. prefilled from the clipboard)
 *   onSubmit     fn({column, op, value})
 *   onCancel     fn()
 */
import { createSignal, Show, For, onMount } from 'solid-js';
import { useDismissable } from '../lib/dismissable.js';

export const FILTER_OPS = [
  { value: '=',            label: '=  (equals)' },
  { value: '<>',           label: '≠  (not equal)' },
  { value: '<',            label: '<  (less than)' },
  { value: '<=',           label: '≤  (less or equal)' },
  { value: '>',            label: '>  (greater than)' },
  { value: '>=',           label: '≥  (greater or equal)' },
  { value: 'like',         label: 'contains' },
  { value: 'is_null',      label: 'is NULL',      noValue: true },
  { value: 'is_not_null',  label: 'is NOT NULL',  noValue: true },
  { value: 'is_empty',     label: 'is empty',     noValue: true },
  { value: 'is_not_empty', label: 'is not empty', noValue: true },
];

export function formatOp(op) {
  const meta = FILTER_OPS.find((o) => o.value === op);
  return meta?.label.split(' ')[0] || op;
}

export default function FilterPopover(props) {
  // Default to "contains" — the most common ad-hoc lookup the user wants when
  // they click a column header. A caller can override (e.g. '=' when prefilling
  // a copied value) and seed the value box (read once at mount; the popover is
  // remounted per open).
  const [op, setOp]       = createSignal(props.initialOp || 'like');
  const [value, setValue] = createSignal(props.initialValue || '');

  const opMeta    = () => FILTER_OPS.find((o) => o.value === op());
  const needsValue = () => !opMeta()?.noValue;

  let valueRef;
  let containerRef;

  const submit = () => {
    props.onSubmit?.({
      column: props.column,
      op:     op(),
      value:  needsValue() ? value() : '',
    });
  };

  useDismissable(() => containerRef, () => props.onCancel?.(), { escape: false });

  onMount(() => {
    // Focus the value input (or operator dropdown if no value needed).
    setTimeout(() => valueRef?.focus(), 0);
  });

  // Anchor under the column header. Clamp to viewport so it stays visible.
  const style = () => {
    const r = props.anchorRect;
    if (!r) return { display: 'none' };
    const popWidth = 280;
    const left = Math.min(r.left, window.innerWidth - popWidth - 8);
    return {
      position: 'fixed',
      top:     `${r.bottom + 4}px`,
      left:    `${Math.max(8, left)}px`,
      width:   `${popWidth}px`,
      'z-index': 1000,
    };
  };

  return (
    <div ref={containerRef} class="filter-popover" style={style()}>
      <div class="filter-popover-header">
        <span class="muted" style="font-size:11px;">Filter on column</span>
        <span class="mono filter-popover-col">{props.column}</span>
      </div>

      <label class="filter-popover-row">
        <span class="filter-popover-label">Operator</span>
        <select
          class="input"
          value={op()}
          onChange={(e) => setOp(e.target.value)}
        >
          <For each={FILTER_OPS}>
            {(o) => <option value={o.value} selected={o.value === op()}>{o.label}</option>}
          </For>
        </select>
      </label>

      <Show when={needsValue()}>
        <label class="filter-popover-row">
          <span class="filter-popover-label">Value</span>
          <input
            ref={valueRef}
            class="input mono"
            type="text"
            value={value()}
            onInput={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  { e.preventDefault(); submit(); }
              if (e.key === 'Escape') { e.preventDefault(); props.onCancel?.(); }
            }}
          />
        </label>
      </Show>

      <div class="filter-popover-footer">
        <button class="btn btn-sm" onClick={submit}>OK</button>
        <button class="btn btn-sm btn-ghost" onClick={() => props.onCancel?.()}>Cancel</button>
      </div>
    </div>
  );
}
